'use client'

import * as React from 'react'
import { Xmark as CancelIcon, Plus as AddIcon, FilterList, WarningCircle } from 'iconoir-react'
import { Select as SelectPrimitive } from 'radix-ui'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { compact } from 'lodash'
import { FilterValue } from 'app/components/shared/filters/FilterValue'
import { Button } from 'app/components/ui/button'
import { default as Combo } from 'app/components/ui/combo/combo'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'app/components/ui/tooltip'
import { FilterValueType, FilterableColumn, getAllowedOperators, getReadableOperator } from 'app/filter.utils'
import { cn } from 'app/lib/utils'
import { OptionType } from 'types/OptionType'
import { useFiltersContainer, type FilterType, type FiltersContainerProps, emptyFilter } from './use-filters-container'

export type { FilterType, FiltersContainerProps }
export { emptyFilter }

/* ─── Joiner ─────────────────────────────────────────────────────────── */

function FilterJoiner({
  join,
  index,
  useOrOption = true,
  hideWherePrefix = false,
  onChange,
}: {
  join: `and` | `or`
  index: number
  useOrOption?: boolean
  hideWherePrefix?: boolean
  onChange: (value: `and` | `or`) => void
}) {
  if (index === 0) return hideWherePrefix ? null : <span className="w-20 px-3 shrink-0">where</span>

  if (useOrOption && index === 1) {
    return (
      <SelectPrimitive.Root value={join} onValueChange={(v) => onChange(v as `and` | `or`)}>
        <SelectPrimitive.Trigger className="flex h-8 w-20 shrink-0 items-center justify-between rounded-lg border border-default bg-surface-raised px-3 align-middle text-body-sm shadow-elevation-1">
          <SelectPrimitive.Value />
          <SelectPrimitive.Icon>
            <ChevronDownIcon className="h-4 w-4" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            className="z-50 w-20 rounded-md border border-default bg-surface-raised shadow-elevation-4"
          >
            <SelectPrimitive.Viewport>
              {[`and`, `or`].map((option) => (
                <SelectPrimitive.Item
                  key={option}
                  value={option}
                  className="flex h-8 cursor-pointer items-center px-2 text-body-sm hover:bg-surface-strong data-[highlighted]:bg-surface-strong"
                >
                  <SelectPrimitive.ItemText>{option}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    )
  }

  return <span className="w-20 px-3 shrink-0">{join}</span>
}

/* ─── Operation ──────────────────────────────────────────────────────── */

function FilterOperation({
  index,
  selectedColumn,
  filters,
  onOperationChange,
}: {
  selectedColumn?: FilterableColumn
  filters?: FilterType[]
  index: number
  onOperationChange: (index: number, option: OptionType) => void
}) {
  if (selectedColumn?.type === FilterValueType.readOnly) return null

  const options = selectedColumn
    ? getAllowedOperators(selectedColumn.type).map((op) => ({
        id: op,
        name: getReadableOperator(op, selectedColumn.type!),
        value: getReadableOperator(op, selectedColumn.type!),
      }))
    : []

  const isDisabled =
    selectedColumn?.readOnly ||
    !!selectedColumn?.uniqOperator ||
    !filters?.[index]?.attribute ||
    (selectedColumn?.type === FilterValueType.rangeDate &&
      getAllowedOperators(FilterValueType.rangeDate).length <= 1) ||
    selectedColumn?.type === FilterValueType.boolean ||
    selectedColumn?.type === FilterValueType.pmsIntegration

  const showChevron = !(
    selectedColumn?.uniqOperator ||
    (selectedColumn?.type === FilterValueType.rangeDate &&
      getAllowedOperators(FilterValueType.rangeDate).length <= 1) ||
    selectedColumn?.type === FilterValueType.boolean ||
    selectedColumn?.type === FilterValueType.pmsIntegration
  )

  // Fall back to the column's default operator so the dropdown still reads e.g.
  // "is" when a term carries no explicit operator (e.g. the channel "Any" state).
  const currentValue = filters?.[index]?.operator ?? selectedColumn?.defaultOperator

  return (
    <SelectPrimitive.Root
      value={currentValue ?? ``}
      onValueChange={(val) => {
        const opt = options.find((o) => o.id === val)
        if (opt) onOperationChange(index, opt)
      }}
      disabled={isDisabled}
    >
      <SelectPrimitive.Trigger className="flex h-8 w-44 shrink-0 items-center justify-between rounded-lg border border-default bg-surface-raised px-2 align-middle text-body-sm shadow-elevation-1 disabled:text-minimal disabled:opacity-70">
        {/* Render the operator text directly rather than via SelectPrimitive.Value,
            which stays blank on first open until its portal items have mounted. */}
        <span className="truncate">
          {currentValue && selectedColumn ? getReadableOperator(currentValue, selectedColumn.type!) : ``}
        </span>
        {showChevron && (
          <SelectPrimitive.Icon>
            <ChevronDownIcon className="h-4 w-4" />
          </SelectPrimitive.Icon>
        )}
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 w-44 rounded-md border border-default bg-surface-raised shadow-elevation-4"
        >
          <SelectPrimitive.Viewport>
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.id}
                value={option.id}
                className="flex h-8 cursor-pointer items-center px-2 text-body-sm hover:bg-surface-strong data-[highlighted]:bg-surface-strong"
              >
                <SelectPrimitive.ItemText>{option.name}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

/* ─── Selector (reuses existing Combo) ───────────────────────────────── */

function FilterSelector({
  columns,
  index,
  selectedColumn,
  filters,
  onSelectorChange,
}: {
  columns?: FilterableColumn[]
  index: number
  selectedColumn?: FilterableColumn
  filters?: FilterType[]
  onSelectorChange: (index: number, option: OptionType) => void
}) {
  const isOptionsGrouped = React.useMemo(() => columns?.some((c) => c.group), [columns])

  const getAvailableColumns = React.useCallback(
    () =>
      compact(
        columns?.map((filter) => ({
          id: filter.attribute,
          name: filter.displayName,
          value: filter.attribute,
          meta: { customComboOption: filter.customComboOption, group: filter.group },
        })) ?? [],
      ),
    [columns],
  )

  return (
    <Combo
      inputClassName="!h-8 cursor-pointer rounded-lg border border-default !px-2 text-body-sm text-strong"
      className={cn(
        selectedColumn && selectedColumn.type === FilterValueType.readOnly ? `w-60` : `w-44`,
        `!z-auto shrink-0`,
      )}
      optionsContainerClassName="flex w-72 max-h-[calc(var(--radix-popover-content-available-height)-10px)] flex-col overflow-hidden"
      value={
        filters?.[index]?.attribute
          ? ({
              id: filters[index]?.attribute,
              name: selectedColumn?.displayName ?? filters[index]?.displayName ?? filters[index]?.attribute,
              value: filters[index]?.attribute,
            } as OptionType)
          : undefined
      }
      options={getAvailableColumns()}
      onChange={(opt) => onSelectorChange(index, opt)}
      showSearchInDropdown
      open={filters?.length === index + 1 && !selectedColumn}
      disabled={selectedColumn?.readOnly}
      showButtonTooltip
      isOptionsGrouped={isOptionsGrouped}
    />
  )
}

/* ─── FiltersContainerView ───────────────────────────────────────────── */

export function FiltersContainerView(props: FiltersContainerProps) {
  const {
    selectedFilters,
    join,
    handleJoinChange,
    handleChange,
    removeFilter,
    addFilter,
    handleSelectorChange,
    handleOperationChange,
  } = useFiltersContainer(props)

  const { columns, useOrOption, hideWherePrefix } = props

  // The card is exactly as tall as one rule row plus the "Add rule" button
  // below it, so the popover does not resize when the first rule is added.
  if (!selectedFilters?.length) {
    return (
      <div className="px-4 py-3">
        <div className="flex h-19 items-center gap-4 rounded-md border border-dashed border-strong bg-surface px-4">
          <FilterList width={24} height={24} strokeWidth={1.5} className="shrink-0 text-minimal" />
          <div className="min-w-0 flex-1">
            <div className="text-body-sm font-semibold tracking-tight text-strong">No filters applied</div>
            <p className="text-body-sm text-subtle">Add a rule to narrow down this list.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={addFilter}>
            <AddIcon width={16} height={16} strokeWidth={1.5} />
            <span>Add rule</span>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col px-4 py-3">
      <div className="flex flex-col space-y-2">
        {selectedFilters.map((filter, index) => {
          const selectedColumn = columns?.find((c) => c.attribute === filter.attribute)
          return (
            <div
              key={`${filter.attribute}-${index}`}
              data-testid="filter-row"
              className="flex flex-col items-start gap-2 align-middle text-body-sm text-strong md:flex-row md:items-center md:gap-0 md:space-x-2"
            >
              <FilterJoiner
                useOrOption={useOrOption}
                hideWherePrefix={hideWherePrefix}
                index={index}
                join={join}
                onChange={handleJoinChange}
              />
              <FilterSelector
                columns={columns?.filter((c) => !c.standalone)}
                index={index}
                selectedColumn={selectedColumn}
                filters={selectedFilters}
                onSelectorChange={handleSelectorChange}
              />
              <FilterOperation
                index={index}
                selectedColumn={selectedColumn}
                filters={selectedFilters}
                onOperationChange={handleOperationChange}
              />
              <FilterValue
                index={index}
                selectedColumn={selectedColumn}
                filters={selectedFilters}
                onValueChange={handleChange}
              />
              {selectedColumn?.readOnly ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <WarningCircle
                        height={20}
                        width={20}
                        className="text-default"
                        data-testid="filter-row-read-only"
                      />
                    </TooltipTrigger>
                    <TooltipContent>Read-only</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <CancelIcon
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="visible cursor-pointer shrink-0"
                  data-testid="filter-row-remove"
                  onClick={() => removeFilter(index)}
                />
              )}
            </div>
          )
        })}
      </div>
      <Button variant="ghost" size="inline" className="mt-1 h-10 self-start pl-1 text-strong" onClick={addFilter}>
        <AddIcon width={16} height={16} strokeWidth={1.5} />
        <span className="text-ui-xs">Add rule</span>
      </Button>
    </div>
  )
}
